/*
 * Ahlam-søgning.
 *
 * Hele matchningen sker i browseren mod indekset fra ahlam-search-index.liquid.
 * Grunden er, at Shopifys søgeindeks ikke kan læse metafelter: butikkens
 * "inspireret af"-navn bor i custom.inspired_by, og et serverkald på "armani si"
 * giver derfor nul træf, selvom VISION er præcis det produkt kunden leder efter.
 *
 * Sidegevinsten er, at resultatet kan opdateres ved hvert tastetryk uden
 * netværkskald, og at vi selv bestemmer rangeringen.
 */
(() => {
  if (customElements.get('ahlam-search')) return;

  /* ---------- tekstnormalisering ---------- */

  // Så "Sauvage Élixir" kan findes med "sauvage elixir", og æøå ikke spærrer.
  // æ og ø har ingen NFD-opløsning og skal skiftes ud i hånden — ellers falder de
  // ud sammen med tegnsætningen, og "Æble" bliver til "ble".
  const fold = (s) =>
    (s || '')
      .toLowerCase()
      .replace(/æ/g, 'ae')
      .replace(/ø/g, 'o')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[’'`]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const tokens = (s) => fold(s).split(' ').filter(Boolean);

  // Levenshtein med loft: vi skal kun vide "er afstanden højst max", så alt over
  // loftet afbrydes tidligt i stedet for at regne hele matricen ud.
  const within = (a, b, max) => {
    if (Math.abs(a.length - b.length) > max) return false;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
      const cur = [i];
      let best = i;
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return false;
      prev = cur;
    }
    return prev[b.length] <= max;
  };

  /* ---------- scoring ---------- */

  // Vægtene afgør rangeringen. inspired ligger højest, fordi det er sådan
  // kunderne leder her: de kender designerduften, ikke Ahlams eget navn.
  const FIELDS = [
    { key: 'i', weight: 100 },
    { key: 't', weight: 90 },
    { key: 'tags', weight: 45 },
    { key: 'type', weight: 20 },
    { key: 'e', weight: 12 },
  ];

  // Hvor godt ét søgeord rammer ét felt. 0 = slet ikke.
  const scoreToken = (token, field) => {
    if (!field) return 0;
    if (field === token) return 1;
    if (field.startsWith(token + ' ')) return 0.95;

    const words = field.split(' ');
    // Ordstart vægter tungere end et tilfældigt sammenfald midt inde i et ord,
    // så "si" rammer "Armani Si" og ikke "Miss Dior".
    for (const w of words) {
      if (w === token) return 0.9;
      if (w.startsWith(token)) return 0.8;
    }
    if (field.includes(token)) return 0.5;

    // Først her tillades stavefejl, og kun på ord der er lange nok til at én
    // fejl ikke gør ordet til et helt andet.
    if (token.length >= 4) {
      for (const w of words) {
        if (w.length >= 4 && within(token, w, token.length >= 7 ? 2 : 1)) return 0.4;
      }
    }
    return 0;
  };

  const scoreProduct = (item, qTokens) => {
    let total = 0;
    for (const token of qTokens) {
      let best = 0;
      for (const f of FIELDS) {
        const s = scoreToken(token, item._f[f.key]);
        if (s) best = Math.max(best, s * f.weight);
      }
      // Alle søgeord skal ramme noget. Ellers ville "armani si" også trække
      // hvert eneste produkt med "si" et vilkårligt sted.
      if (!best) return 0;
      total += best;
    }
    // Et kort felt der rammes helt er et bedre svar end et langt der rammes
    // delvist, så præcision belønnes let.
    if (item._f.i && fold(item._f.i).startsWith(qTokens.join(' '))) total += 30;
    if (item._f.t && fold(item._f.t).startsWith(qTokens.join(' '))) total += 25;
    if (!item.avail) total -= 15;
    return total;
  };

  /* ---------- forslag i venstre kolonne ---------- */

  // Forslagene er ikke frit opfundne: de er de faktiske navne fra kataloget, så
  // et klik altid fører til noget.
  const buildPhrases = (index) => {
    const seen = new Map();
    for (const item of index) {
      for (const raw of [item.i, item.t]) {
        const phrase = (raw || '').trim();
        if (!phrase) continue;
        const key = fold(phrase);
        if (!key || seen.has(key)) continue;
        seen.set(key, phrase);
      }
    }
    return [...seen.entries()].map(([key, label]) => ({ key, label }));
  };

  const matchPhrases = (phrases, qTokens, limit) => {
    const q = qTokens.join(' ');
    const out = [];
    for (const p of phrases) {
      let score = 0;
      if (p.key.startsWith(q)) score = 100 - p.key.length;
      else if (p.key.split(' ').some((w) => w.startsWith(qTokens[qTokens.length - 1]))) score = 60 - p.key.length;
      else if (p.key.includes(q)) score = 40 - p.key.length;
      if (score) out.push({ ...p, score });
    }
    return out.sort((a, b) => b.score - a.score).slice(0, limit);
  };

  /* ---------- rendering ---------- */

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );

  // Fremhæver den del af forslaget kunden allerede har skrevet, som på
  // referencen: det skrevne står tydeligt, resten træder tilbage.
  const highlight = (label, query) => {
    const f = fold(label);
    const q = fold(query);
    if (!q) return esc(label);

    let start = f.startsWith(q) ? 0 : f.indexOf(' ' + q);
    if (start > -1 && f[start] === ' ') start += 1;
    if (start < 0) return esc(label);

    // fold() kan ændre længden (accenter, tegnsætning), så positionen mappes
    // tilbage til den rå streng ved at tælle foldede tegn frem.
    let rawStart = -1;
    let rawEnd = -1;
    let count = 0;
    for (let i = 0; i <= label.length; i++) {
      const folded = fold(label.slice(0, i));
      if (folded.length > count) {
        count = folded.length;
        if (count === start + 1 && rawStart < 0) rawStart = i - 1;
        if (count === start + q.length) { rawEnd = i; break; }
      }
    }
    if (rawStart < 0 || rawEnd < 0) return esc(label);

    return (
      esc(label.slice(0, rawStart)) +
      '<mark class="ahlam-search__mark">' + esc(label.slice(rawStart, rawEnd)) + '</mark>' +
      esc(label.slice(rawEnd))
    );
  };

  class AhlamSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-ahlam-input]');
      this.panel = this.querySelector('[data-ahlam-panel]');
      this.defaultPane = this.querySelector('[data-ahlam-default]');
      this.resultsPane = this.querySelector('[data-ahlam-results]');
      this.termList = this.querySelector('[data-ahlam-terms]');
      this.grid = this.querySelector('[data-ahlam-grid]');
      this.emptyPane = this.querySelector('[data-ahlam-empty]');
      this.reset = this.querySelector('[data-ahlam-reset]');
      this.countEl = this.querySelector('[data-ahlam-count]');
      if (!this.input || !this.panel) return;

      // Må ikke hedde this.prefix: Element.prefix er skrivebeskyttet i DOM'et,
      // og en tildeling dér kaster og dræber hele connectedCallback.
      this.inspiredPrefix = this.dataset.inspiredPrefix || 'Inspireret af';
      // routes.search_url baerer sprog- og markedspraefikset. Et hardkodet
      // '/search' ville tabe det og sende kunden til en 404 paa et andet marked.
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.index = [];
      this.phrases = [];
      this.loadIndex();

      this.onInput = this.onInput.bind(this);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', () => this.open());
      this.addEventListener('keydown', (e) => this.onKeydown(e));

      // Uden det her lukker feltet sig selv, i det øjeblik man trykker musen ned
      // på et resultat: fokus forlader inputtet, før klikket når at blive udløst.
      this.addEventListener('mousedown', (e) => {
        if (e.target !== this.input) e.preventDefault();
      });

      if (this.reset) {
        this.reset.addEventListener('click', () => {
          this.input.value = '';
          this.onInput();
          this.input.focus();
        });
      }

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.close();
      });

      if (this.input.value.trim()) this.onInput();
    }

    loadIndex() {
      const node = document.querySelector('[data-ahlam-search-index]');
      if (!node) return;
      try {
        this.index = JSON.parse(node.textContent) || [];
      } catch (err) {
        this.index = [];
        return;
      }
      // Foldes én gang og genbruges. Ellers ville hvert tastetryk normalisere
      // hele kataloget forfra.
      for (const item of this.index) {
        item._f = {
          i: fold(item.i),
          t: fold(item.t),
          tags: fold(item.tags),
          type: fold(item.type),
          e: fold(item.e),
        };
      }
      this.phrases = buildPhrases(this.index);
    }

    open() {
      this.panel.hidden = false;
      this.classList.add('is-open');
      // aria-expanded hoerer paa comboboxen, altsaa inputtet. Paa vaertselementet
      // ville en skaermlaeser aldrig knytte tilstanden til feltet.
      this.input.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('ahlam-search-open');
    }

    close() {
      this.panel.hidden = true;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('ahlam-search-open');
    }

    onInput() {
      const raw = this.input.value;
      const q = raw.trim();
      if (this.reset) this.reset.classList.toggle('hidden', !q);
      this.open();

      if (!q) {
        if (this.defaultPane) this.defaultPane.hidden = false;
        if (this.resultsPane) this.resultsPane.hidden = true;
        if (this.emptyPane) this.emptyPane.hidden = true;
        return;
      }

      const qTokens = tokens(q);
      if (!qTokens.length) return;

      const hits = this.index
        .map((item) => ({ item, score: scoreProduct(item, qTokens) }))
        .filter((r) => r.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 12);

      if (this.defaultPane) this.defaultPane.hidden = true;

      if (!hits.length) {
        if (this.resultsPane) this.resultsPane.hidden = true;
        if (this.emptyPane) {
          this.emptyPane.hidden = false;
          const slot = this.emptyPane.querySelector('[data-ahlam-empty-template]');
          if (slot) slot.textContent = (slot.dataset.ahlamEmptyTemplate || '').replace('%%TERM%%', q);
        }
        return;
      }

      if (this.emptyPane) this.emptyPane.hidden = true;
      if (this.resultsPane) this.resultsPane.hidden = false;

      this.renderTerms(matchPhrases(this.phrases, qTokens, 10), raw);
      this.renderProducts(hits.map((h) => h.item));
    }

    renderTerms(matches, query) {
      if (!this.termList) return;
      if (!matches.length) {
        this.termList.innerHTML = '';
        this.termList.closest('[data-ahlam-terms-col]')?.setAttribute('hidden', '');
        return;
      }
      this.termList.closest('[data-ahlam-terms-col]')?.removeAttribute('hidden');
      this.termList.innerHTML = matches
        .map(
          (m) => `<li>
            <a class="ahlam-search__term" role="option" href="${esc(this.searchUrl)}?q=${encodeURIComponent(m.label)}&options%5Bprefix%5D=last">
              <span class="ahlam-search__term-icon svg-wrapper" aria-hidden="true">
                <svg viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.03 11.68A5.784 5.784 0 112.85 3.5a5.784 5.784 0 018.18 8.18zm.26 1.12a6.78 6.78 0 11.72-.7l5.4 5.4a.5.5 0 11-.71.7l-5.41-5.4z" fill="currentColor"/></svg>
              </span>
              <span class="ahlam-search__term-label">${highlight(m.label, query)}</span>
            </a>
          </li>`
        )
        .join('');
    }

    renderProducts(items) {
      if (!this.grid) return;
      if (this.countEl) this.countEl.textContent = String(items.length);
      this.grid.innerHTML = items
        .map((item) => {
          const img = item.img
            ? `<img class="ahlam-search__thumb" src="${esc(item.img)}" alt="${esc(item.t)}" loading="lazy" width="200" height="200">`
            : '<span class="ahlam-search__thumb ahlam-search__thumb--empty"></span>';
          // Kravet er, at "inspireret af" står på hvert eneste produktkort, også
          // her i søgningen. Bundles har med vilje ingen værdi og får ingen linje.
          const inspired = item.i
            ? `<span class="ahlam-inspired-by ahlam-inspired-by--result">
                 <span class="ahlam-inspired-by__prefix">${esc(this.inspiredPrefix)}</span>
                 <span class="ahlam-inspired-by__name">${esc(item.i)}</span>
               </span>`
            : '';
          const excerpt = item.e ? `<span class="ahlam-search__excerpt">${esc(item.e)}</span>` : '';
          return `<a class="ahlam-search__product" role="option" href="${esc(item.u)}">
            <span class="ahlam-search__thumb-wrap">${img}</span>
            <span class="ahlam-search__product-text">
              <span class="ahlam-search__product-title">${esc(item.t)}</span>
              ${excerpt}
              ${inspired}
              <span class="ahlam-search__product-price">${esc(item.p)}</span>
            </span>
          </a>`;
        })
        .join('');
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        this.input.blur();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const links = [...this.panel.querySelectorAll('a:not([hidden])')].filter(
        (a) => a.offsetParent !== null
      );
      if (!links.length) return;
      e.preventDefault();
      const at = links.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? at + 1 : at - 1;
      if (next < 0) this.input.focus();
      else if (links[next]) links[next].focus();
    }
  }

  customElements.define('ahlam-search', AhlamSearch);

  /*
   * Resultatsiden på /search bruger den samme matcher.
   *
   * Den er nødt til det: trykker kunden Enter, går forespørgslen til Shopifys
   * eget søgeindeks, og det kan stadig ikke se metafeltet. Uden det her ville
   * "armani si" virke i panelet og så give nul træf et sekund senere.
   */
  window.AhlamSearch = {
    fold,
    tokens,
    match(query, index) {
      const qTokens = tokens(query);
      if (!qTokens.length) return [];
      return index
        .map((item) => {
          if (!item._f) {
            item._f = {
              i: fold(item.i), t: fold(item.t), tags: fold(item.tags),
              type: fold(item.type), e: fold(item.e),
            };
          }
          return { item, score: scoreProduct(item, qTokens) };
        })
        .filter((r) => r.score > 0)
        .sort((a, b) => b.score - a.score)
        .map((r) => r.item);
    },
    readIndex() {
      const node = document.querySelector('[data-ahlam-search-index]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent) || [];
      } catch (err) {
        return [];
      }
    },
  };
})();
